<template>
  <div v-if="adminMode" class="admin-page tasks-admin-page">
    <div class="admin-page-inner">
      <header class="admin-page-header tasks-admin-header">
        <div>
          <span class="admin-kicker">Aufträge &amp; Planung</span>
          <h1 class="admin-page-title">Aufgaben</h1>
          <p class="admin-page-subtitle">
            Aufgaben zentral verwalten, zuweisen und nachverfolgen.
          </p>
        </div>
        <button type="button" class="admin-button--primary" @click="setNew()">
          <i class="fa fa-plus mr-2" aria-hidden="true"></i>Neue Aufgabe
        </button>
      </header>

      <div class="task-stat-grid" aria-label="Aufgabenübersicht">
        <div class="task-stat task-stat--open">
          <span class="task-stat__icon"><i class="fa fa-inbox" aria-hidden="true"></i></span>
          <div>
            <span class="task-stat__label">Offene Aufgaben</span>
            <strong>{{ taskCounts.open }}</strong>
          </div>
        </div>
        <div class="task-stat task-stat--progress">
          <span class="task-stat__icon"><i class="fa fa-hourglass-half" aria-hidden="true"></i></span>
          <div>
            <span class="task-stat__label">In Bearbeitung</span>
            <strong>{{ taskCounts.inProgress }}</strong>
          </div>
        </div>
        <div class="task-stat task-stat--done">
          <span class="task-stat__icon"><i class="fa fa-circle-check" aria-hidden="true"></i></span>
          <div>
            <span class="task-stat__label">Erledigt</span>
            <strong>{{ taskCounts.closed }}</strong>
          </div>
        </div>
      </div>

      <section class="admin-panel tasks-panel">
        <div class="tasks-panel__header">
          <div>
            <span class="admin-kicker">Arbeitsbereich</span>
            <h2 class="admin-panel__title">Aufgabenübersicht</h2>
          </div>
          <div class="admin-tabs" role="tablist" aria-label="Aufgabenstatus">
            <button
              type="button"
              class="admin-tab"
              :class="{ 'admin-tab--active': showOpenTasks }"
              :aria-selected="showOpenTasks"
              role="tab"
              @click="setTasks()"
            >
              Offen <span class="task-tab-count">{{ taskCounts.open }}</span>
            </button>
            <button
              type="button"
              class="admin-tab"
              :class="{ 'admin-tab--active': showSolvedTasks }"
              :aria-selected="showSolvedTasks"
              role="tab"
              @click="setClosed()"
            >
              Erledigt <span class="task-tab-count">{{ taskCounts.closed }}</span>
            </button>
            <button
              type="button"
              class="admin-tab"
              :class="{ 'admin-tab--active': showNew }"
              :aria-selected="showNew"
              role="tab"
              @click="setNew()"
            >
              <i class="fa fa-plus mr-1" aria-hidden="true"></i>Neu
            </button>
          </div>
        </div>

        <form v-if="showNew" class="task-form" @submit.prevent="createTask()">
          <div v-if="error" class="task-form__error" role="alert">
            <i class="fa fa-circle-exclamation mr-2" aria-hidden="true"></i>{{ error }}
          </div>
          <div class="task-form__grid">
            <label class="task-form__field">
              <span>Titel</span>
              <input v-model="newTask.summary" required placeholder="Worum geht es?" />
            </label>
            <label class="task-form__field">
              <span>Zuweisen</span>
              <select v-model="newTask.assignedTo">
                <option value="">Nicht zugewiesen</option>
                <option
                  v-for="contact in taskContacts"
                  :key="contact.id"
                  :value="contact.id"
                >
                  {{ contact.name }} {{ contact.surname }}
                </option>
              </select>
            </label>
            <label class="task-form__field task-form__field--wide">
              <span>Beschreibung</span>
              <textarea
                v-model="newTask.description"
                rows="5"
                placeholder="Details, Kontext oder nächste Schritte"
                @focus="resizeTextarea"
                @keyup="resizeTextarea"
              ></textarea>
            </label>
          </div>
          <div class="task-files">
            <div>
              <span class="task-form__label">Dateien</span>
              <p class="task-form__hint">Optional: ergänzende Unterlagen zur Aufgabe.</p>
            </div>
            <div class="task-files__items">
              <span v-for="file in newTask.files" :key="file.name" class="task-file">
                <i class="fa fa-paperclip mr-1" aria-hidden="true"></i>{{ file.name }}
              </span>
              <button type="button" class="admin-button--secondary admin-button--sm" @click="addFile()">
                <i class="fa fa-plus mr-1" aria-hidden="true"></i>Datei hinzufügen
              </button>
            </div>
          </div>
          <div class="task-form__actions">
            <button type="button" class="admin-button--secondary" @click="setTasks()">Abbrechen</button>
            <button type="submit" class="admin-button--primary">
              <i class="fa fa-check mr-2" aria-hidden="true"></i>Aufgabe erstellen
            </button>
          </div>
        </form>

        <div v-else class="task-list">
          <div v-show="loading" class="task-list__state">
            <i class="fa fa-spinner fa-spin mr-2" aria-hidden="true"></i>Aufgaben werden geladen ...
          </div>
          <div v-show="!loading && tasks.length === 0" class="task-list__state task-list__state--empty">
            <span class="task-list__empty-icon"><i class="fa fa-check" aria-hidden="true"></i></span>
            <strong>{{ showSolvedTasks ? "Keine erledigten Aufgaben" : "Keine offenen Aufgaben" }}</strong>
            <span>{{ showSolvedTasks ? "Hier ist alles aufgeräumt." : "Aktuell gibt es nichts zu bearbeiten." }}</span>
          </div>
          <div v-show="!loading && tasks.length > 0" class="task-list__cards">
            <article
              v-for="task in tasks"
              :key="task.id"
              class="task-card"
              tabindex="0"
              @click="viewTask(task)"
              @keydown.enter="viewTask(task)"
            >
              <div class="task-card__topline">
                <div class="task-card__meta">
                  <span :class="statusClass(task.status)">{{ task.status }}</span>
                  <span class="task-card__date">
                    <i class="fa fa-calendar-day mr-1" aria-hidden="true"></i>{{ formatTaskDate(task.createdAt) }}
                  </span>
                </div>
                <div class="task-card__actions">
                  <button
                    type="button"
                    class="admin-button--ghost admin-button--icon"
                    title="Aufgabe öffnen"
                    aria-label="Aufgabe öffnen"
                    @click.stop="viewTask(task)"
                  >
                    <i class="fa fa-arrow-up-right-from-square" aria-hidden="true"></i>
                  </button>
                  <button
                    type="button"
                    class="admin-button--ghost admin-button--icon task-card__delete"
                    title="Aufgabe löschen"
                    aria-label="Aufgabe löschen"
                    :disabled="deletingTaskId === task.id"
                    @click.stop="deleteTask(task)"
                  >
                    <i
                      class="fa"
                      :class="deletingTaskId === task.id ? ['fa-spinner', 'fa-spin'] : ['fa-trash']"
                      aria-hidden="true"
                    ></i>
                  </button>
                </div>
              </div>
              <h3 class="task-card__title">{{ task.summary || "Ohne Titel" }}</h3>
              <p class="task-card__description">{{ task.description || "Keine Beschreibung hinterlegt." }}</p>
              <div class="task-card__footer">
                <span :class="{ 'task-card__assignee--unassigned': task.assignedTo == null }">
                  <i class="fa fa-user mr-2" aria-hidden="true"></i>
                  {{ task.assignedTo == null ? "Nicht zugewiesen" : task.assignedName }}
                </span>
                <span class="task-card__open-label">Details ansehen <i class="fa fa-arrow-right ml-1" aria-hidden="true"></i></span>
              </div>
            </article>
          </div>
        </div>
      </section>
    </div>
  </div>

  <template v-else>
    <toolbar
      title="Aufgaben"
      :showReload="true"
      @reload="getOpenTasks()"
      :backUrl="localStorage ? localStorage.getItem('last') : '/'"
    />
    <div class="w-full text-center">
      <div class="w-full md:w-5/6 inline-block text-left">
        <Spinner v-if="loading" />
        <div class="tab">
          <button
            style="flex-grow: 4; font-size: 15px"
            :class="{ tabactive: showOpenTasks }"
            class="singletab"
            @click="setTasks()"
          >
            Offen
          </button>
          <button
            style="font-size: 15px"
            :class="{ tabactive: showSolvedTasks }"
            class="singletab"
            @click="setClosed()"
          >
            Erledigt
          </button>
          <button
            style="font-size: 15px"
            :class="{ tabactive: showNew }"
            class="text-red-500 singletab"
            @click="setNew()"
          >
            Neu
          </button>
        </div>
        <div v-if="showOpenTasks || showSolvedTasks" style="padding: 3px">
          <div v-if="tasks.length === 0" class="pt-10 text-center">
            <span class="font-bold inline-block">Aktuell keine offenen Aufgaben</span>
          </div>
          <template v-for="task in tasks" :key="task.id">
            <div class="w-full p-2 border-b border-black" @click="viewTask(task)">
              <span class="font-bold text-xl">{{ task.summary }}</span>
              <div
                class="text-sm font-bold inline-block p-1 rounded ml-2"
                :class="{
                  'text-red-800 bg-red-200 border-red-800': task.status === 'Offen',
                  'text-yellow-800 bg-yellow-200 border-yellow-800': task.status === 'In Bearbeitung',
                  'text-green-800 bg-green-200 border-green-800': task.status === 'Erledigt',
                }"
              >
                {{ task.status }}
              </div>
              <div>
                <i class="fa fa-user"></i>
                <span v-if="task.assignedTo == null" class="pl-1 font-bold text-red-500">Nicht zugewiesen</span>
                <span v-else class="pl-1">{{ task.assignedName }}</span>
              </div>
              <div class="pt-2">
                <pre class="font-sans w-full whitespace-pre-wrap">{{ task.description }}</pre>
              </div>
            </div>
          </template>
        </div>
        <div v-if="showNew">
          <div class="job" style="padding: 15px; display: block">
            <div v-if="error"><h3 style="color: red">{{ error }}</h3></div>
            <div style="margin-top: 15px; margin-bottom: 15px">
              <div><span style="font-weight: bold">Titel:</span></div>
              <input v-model="newTask.summary" class="px-2 py-1 w-full" />
            </div>
            <div style="margin-top: 15px; margin-bottom: 15px">
              <span style="display: inline-block; font-weight: bold; margin-bottom: 5px">Beschreibung:</span>
              <div>
                <textarea
                  v-model="newTask.description"
                  class="p-2 box-border w-full"
                  rows="3"
                  @focus="resizeTextarea"
                  @keyup="resizeTextarea"
                ></textarea>
              </div>
              <span style="font-weight: bold">Zuweisen:</span>
              <select v-model="newTask.assignedTo" class="w-full px-2 py-1">
                <option value="">Nicht zugewiesen</option>
                <option v-for="contact in taskContacts" :key="contact.id" :value="contact.id">
                  {{ contact.name }} {{ contact.surname }}
                </option>
              </select>
            </div>
            <div>
              <span class="font-bold inline-block">Dateien:</span><br />
              <div v-for="file in newTask.files" :key="file.name" class="m-2 inline-block">
                <div class="text-blue-500 font-bold border-b-2 border-blue-500 mr-2">{{ file.name }}</div>
              </div>
              <div
                class="font-bold px-6 border-4 border-blue-500 rounded text-blue-500 inline-block m-2 cursor-pointer"
                @click="addFile()"
              >
                +
              </div>
            </div>
            <div class="w-full text-right">
              <button
                class="bg-white px-3 py-2 border-2 rounded-lg border-blue-500 text-blue-500"
                @click="createTask()"
              >
                Erstellen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
</template>
<script>
import CASP from "../../CASP";
import Spinner from "@/components/Spinner";
import { useCookies } from "vue3-cookies";
import Toolbar from "@/components/Toolbar.vue";

export default {
  props: {
    adminMode: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { cookies } = useCookies();
    return { cookies };
  },
  name: "Jobs",
  components: {
    Spinner,
    Toolbar,
  },
  data() {
    return {
      newTask: {
        assignedTo: "",
        files: [],
      },
      showOpenTasks: true,
      showSolvedTasks: false,
      showNew: false,
      taskContacts: [],
      tasks: [],
      openTasks: [],
      closedTasks: [],
      closedTasksLoaded: false,
      taskCounts: {
        open: 0,
        inProgress: 0,
        closed: 0,
      },
      deletingTaskId: null,
      token: "",
      error: false,
      loading: true,
      localStorage: localStorage,
    };
  },
  methods: {
    addFile() {
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.addEventListener("change", (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
          this.newTask.files.push({ name: file.name, data: event.target.result });
        };
        reader.readAsDataURL(file);
      });
      fileInput.click();
    },
    viewTask(task) {
      if (this.adminMode) {
        this.$router.push({
          name: "AdminTask",
          params: { id: task.id },
          query: { status: this.showSolvedTasks ? "closed" : "open" },
        });
      } else {
        window.location = "/tasks/" + task.id;
      }
    },
    hideAll() {
      this.showOpenTasks = false;
      this.showNew = false;
      this.showSolvedTasks = false;
    },
    setNew() {
      this.hideAll();
      this.showNew = true;
      this.error = false;
    },
    setClosed() {
      this.hideAll();
      this.showSolvedTasks = true;
      if (this.closedTasksLoaded) {
        this.tasks = this.closedTasks;
      } else {
        this.getClosedTasks();
      }
    },
    setTasks() {
      this.hideAll();
      this.getOpenTasks();
      this.showOpenTasks = true;
    },
    getOpenTasks() {
      this.loading = true;
      CASP.getTasks("open", (status, tasks) => {
        const loadedTasks = Array.isArray(tasks) ? tasks : [];
        this.openTasks = loadedTasks;
        this.taskCounts.open = loadedTasks.length;
        this.taskCounts.inProgress = loadedTasks.filter(
          (task) => task.status === "In Bearbeitung"
        ).length;
        if (this.showOpenTasks) this.tasks = loadedTasks;
        this.loading = false;

        // Load the second tab in the background so its count is available immediately.
        if (this.adminMode && !this.closedTasksLoaded) this.loadClosedTasks(false);
      });
    },
    getClosedTasks() {
      this.loadClosedTasks(true);
    },
    refreshCurrentTab() {
      if (this.showSolvedTasks) {
        this.getClosedTasks();
      } else {
        this.getOpenTasks();
      }
    },
    loadClosedTasks(updateVisibleTab) {
      this.loading = true;
      CASP.getTasks("closed", (status, tasks) => {
        const loadedTasks = Array.isArray(tasks) ? tasks : [];
        this.closedTasks = loadedTasks;
        this.closedTasksLoaded = true;
        this.taskCounts.closed = loadedTasks.length;
        if (updateVisibleTab && this.showSolvedTasks) this.tasks = loadedTasks;
        this.loading = false;
      });
    },
    resizeTextarea(e) {
      const area = e.target;
      area.style.overflow = "hidden";
      area.style.height = area.scrollHeight + "px";
    },
    createTask() {
      this.error = false;
      CASP.createTask(this.newTask, (status, response) => {
        if (status == 200) {
          this.newTask = { assignedTo: "", files: [] };
          this.setTasks();
        } else {
          this.error = response?.error || "Die Aufgabe konnte nicht erstellt werden.";
        }
      });
    },
    deleteTask(task) {
      if (!window.confirm(`Aufgabe "${task.summary || "Ohne Titel"}" wirklich löschen?`)) return;

      this.deletingTaskId = task.id;
      CASP.deleteTask(task.id, (status, response) => {
        if (status === 200) {
          this.deletingTaskId = null;
          this.refreshCurrentTab();
          return;
        } else {
          window.alert(response?.desc || "Die Aufgabe konnte nicht gelöscht werden.");
        }
        this.deletingTaskId = null;
      });
    },
    statusClass(status) {
      return {
        "task-status": true,
        "task-status--open": status === "Offen",
        "task-status--progress": status === "In Bearbeitung",
        "task-status--done": status === "Erledigt",
      };
    },
    formatTaskDate(date) {
      if (!date) return "Kein Erstellungsdatum";
      const parsedDate = new Date(date);
      if (Number.isNaN(parsedDate.getTime())) return "Kein Erstellungsdatum";
      return parsedDate.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },
  },
  created: function () {
    const mainSpinner = document.getElementById("mainspinner");
    if (mainSpinner) mainSpinner.style.display = "none";
    if (this.cookies.get("caspauth") || this.cookies.get("authtoken")) {
      this.token = this.cookies.get("caspauth") || this.cookies.get("authtoken");
    } else {
      window.location = "/forbidden";
    }
    if (this.adminMode && this.$route.query.status === "closed") {
      this.showOpenTasks = false;
      this.showSolvedTasks = true;
      this.getClosedTasks();
    } else {
      this.getOpenTasks();
    }
    let comp = this;
    CASP.getTaskContacts((status, taskContacts) => {
      comp.taskContacts = (Array.isArray(taskContacts) ? taskContacts : []).filter(
        (contact) => !["PSL1", "PSL3"].includes(contact.persnr)
      );
    });
  },
};
</script>
<style scoped>
.tab {
  overflow: hidden;
  background-color: white;
  display: flex;
}

input:focus {
  outline: none;
  border: unset;
  border-bottom: 2px solid rgb(0, 122, 255);
}

input,
textarea {
  border: unset;
  outline: none;
  border-bottom: 1px solid black;
}

textarea:focus {
  border-bottom: 1px solid rgb(0, 122, 255);
  outline: none;
}

.small {
  flex-grow: 2;
}

.large {
  flex-grow: 1;
}

button {
  font-weight: bold;
}

.unread {
  color: rgb(245, 0, 0);
}

/* Style the buttons that are used to open the tab content */
.singletab {
  color: gray;
  border: none;
  outline: none;
  cursor: pointer;
  background: inherit;
  padding: 14px 16px;
  transition: 0.3s;
  flex-grow: 1;
  border-bottom: 3px solid gray;
}

/* Change background color of buttons on hover
.tab button:hover {
  background-color: rgb(37, 37, 37);
}*/

/* Create an active/current tablink class */
.tabactive {
  color: rgb(0, 122, 255);
  border-bottom: 3px solid rgb(0, 122, 255);
}

/* Style the tab content */
.tabcontent {
  padding: 6px 12px;
  border: 1px solid #ccc;
  border-top: none;
}

.tasks-admin-page {
  background:
    radial-gradient(circle at 92% 0%, rgba(254, 237, 1, 0.13), transparent 26rem),
    var(--admin-background);
}

.tasks-admin-header {
  align-items: center;
}

.tasks-admin-header > button {
  flex: 0 0 auto;
}

.task-stat-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.9rem;
  margin-bottom: 1.25rem;
}

.task-stat {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-height: 6.2rem;
  border: 1px solid var(--admin-line);
  border-left: 4px solid var(--admin-line-strong);
  border-radius: 0.75rem;
  background: var(--admin-surface);
  padding: 1rem 1.15rem;
  box-shadow: 0 1px 2px rgba(23, 26, 31, 0.04);
}

.task-stat--open {
  border-left-color: #e2b100;
}

.task-stat--progress {
  border-left-color: #4769a5;
}

.task-stat--done {
  border-left-color: #3f9a72;
}

.task-stat__icon {
  display: inline-flex;
  width: 2.65rem;
  height: 2.65rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.65rem;
  background: #fff8c4;
  color: #8b7200;
  font-size: 1.1rem;
}

.task-stat--progress .task-stat__icon {
  background: #e9effa;
  color: #4769a5;
}

.task-stat--done .task-stat__icon {
  background: #e8f5ee;
  color: #287a55;
}

.task-stat__label {
  display: block;
  color: var(--admin-muted);
  font-size: 0.82rem;
  font-weight: 700;
}

.task-stat strong {
  display: block;
  margin-top: 0.1rem;
  font-size: 1.75rem;
  line-height: 1;
}

.tasks-panel {
  overflow: hidden;
}

.tasks-panel__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px solid var(--admin-line);
  padding: 1rem 1.25rem 0;
}

.tasks-panel__header .admin-tabs {
  margin-bottom: -1px;
}

.task-tab-count {
  display: inline-flex;
  min-width: 1.35rem;
  height: 1.35rem;
  align-items: center;
  justify-content: center;
  margin-left: 0.35rem;
  border-radius: 999px;
  background: #edf0f4;
  color: var(--admin-muted);
  font-size: 0.72rem;
  line-height: 1;
}

.admin-tab--active .task-tab-count {
  background: var(--admin-yellow);
  color: var(--admin-ink);
}

.task-list {
  display: grid;
  gap: 0.7rem;
  padding: 1rem 1.25rem 1.25rem;
}

.task-list__cards {
  display: grid;
  gap: 0.7rem;
}

.task-list__state {
  padding: 3rem 1rem;
  color: var(--admin-muted);
  text-align: center;
}

.task-list__state--empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
}

.task-list__state--empty strong {
  color: var(--admin-ink);
  font-size: 1rem;
}

.task-list__empty-icon {
  display: inline-flex;
  width: 3rem;
  height: 3rem;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
  border-radius: 50%;
  background: #e8f5ee;
  color: #287a55;
  font-size: 1.25rem;
}

.task-card {
  cursor: pointer;
  border: 1px solid var(--admin-line);
  border-radius: 0.7rem;
  background: #ffffff;
  padding: 1rem 1.1rem;
  transition: border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease;
}

.task-card:hover,
.task-card:focus-visible {
  border-color: var(--admin-line-strong);
  box-shadow: 0 0.35rem 1rem rgba(23, 26, 31, 0.08);
  outline: none;
  transform: translateY(-1px);
}

.task-card__topline,
.task-card__meta,
.task-card__footer,
.task-card__actions {
  display: flex;
  align-items: center;
}

.task-card__topline,
.task-card__footer {
  justify-content: space-between;
  gap: 0.8rem;
}

.task-card__meta {
  flex-wrap: wrap;
  gap: 0.65rem;
}

.task-card__date,
.task-card__footer {
  color: var(--admin-muted);
  font-size: 0.78rem;
}

.task-status {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.27rem 0.6rem;
  font-size: 0.73rem;
  font-weight: 800;
}

.task-status--open {
  background: #fff4d1;
  color: #805f00;
}

.task-status--progress {
  background: #e9effa;
  color: #35578f;
}

.task-status--done {
  background: #e8f5ee;
  color: #287a55;
}

.task-card__actions {
  gap: 0.1rem;
}

.task-card__actions button {
  min-height: 2.1rem;
  width: 2.1rem;
}

.task-card__delete:hover:not(:disabled) {
  background: #fceaea !important;
  color: var(--admin-danger) !important;
}

.task-card__title {
  margin: 0.65rem 0 0.25rem;
  color: var(--admin-ink);
  font-size: 1.05rem;
  font-weight: 800;
}

.task-card__description {
  display: -webkit-box;
  min-height: 1.35rem;
  margin: 0 0 0.9rem;
  overflow: hidden;
  color: #4d5664;
  font-size: 0.9rem;
  line-height: 1.5;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.task-card__footer {
  border-top: 1px solid #eef0f3;
  padding-top: 0.7rem;
}

.task-card__assignee--unassigned {
  color: var(--admin-danger);
  font-weight: 700;
}

.task-card__open-label {
  color: #314f90;
  font-weight: 700;
}

.task-form {
  padding: 1.4rem 1.25rem 1.25rem;
}

.task-form__error {
  margin-bottom: 1rem;
  border: 1px solid #e7b7b7;
  border-radius: 0.5rem;
  background: #fff2f2;
  color: #9d2f2f;
  padding: 0.7rem 0.85rem;
  font-weight: 700;
}

.task-form__grid {
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(13rem, 1fr);
  gap: 1rem;
}

.task-form__field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  color: var(--admin-ink);
  font-weight: 800;
}

.task-form__field--wide {
  grid-column: 1 / -1;
}

.task-form__field input,
.task-form__field select,
.task-form__field textarea {
  width: 100%;
  border: 1px solid var(--admin-line-strong);
  border-radius: 0.45rem;
  background: #ffffff;
  color: var(--admin-ink);
  padding: 0.65rem 0.75rem;
  font: inherit;
  font-size: 0.92rem;
  font-weight: 400;
}

.task-form__field input:focus,
.task-form__field select:focus,
.task-form__field textarea:focus {
  border-color: #7e8da5;
  outline: 3px solid rgba(254, 237, 1, 0.3);
}

.task-form__field textarea {
  resize: vertical;
}

.task-files {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.25rem;
  border-top: 1px solid var(--admin-line);
  padding-top: 1rem;
}

.task-form__label {
  display: block;
  font-weight: 800;
}

.task-form__hint {
  margin: 0.2rem 0 0;
  color: var(--admin-muted);
  font-size: 0.8rem;
}

.task-files__items {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.45rem;
}

.task-file {
  border-radius: 0.35rem;
  background: #eef2f7;
  color: #314f90;
  padding: 0.4rem 0.55rem;
  font-size: 0.8rem;
  font-weight: 700;
}

.task-form__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.55rem;
  margin-top: 1.5rem;
  border-top: 1px solid var(--admin-line);
  padding-top: 1rem;
}

@media (max-width: 720px) {
  .tasks-admin-page {
    padding: 1rem;
  }

  .tasks-admin-header,
  .tasks-panel__header,
  .task-files {
    align-items: stretch;
    flex-direction: column;
  }

  .tasks-admin-header > button {
    width: 100%;
  }

  .task-stat-grid {
    grid-template-columns: 1fr;
  }

  .tasks-panel__header {
    padding-top: 1rem;
  }

  .tasks-panel__header .admin-tabs {
    margin: 0;
  }

  .task-form__grid {
    grid-template-columns: 1fr;
  }

  .task-files__items {
    justify-content: flex-start;
  }

  .task-form__actions {
    flex-direction: column-reverse;
  }

  .task-form__actions button {
    width: 100%;
  }

  .task-card__open-label {
    display: none;
  }
}
</style>